import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { mainSponsors as MAIN_SPONSORS, goldSponsors as GOLD_SPONSORS, officialSponsors as OFFICIAL_SPONSORS } from "./sponsorsData";

function SponsorsPartners() {
  const { t, i18n } = useTranslation();
  const [visibleItems, setVisibleItems] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    const items = containerRef.current?.querySelectorAll(
      "[data-index]"
    );
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  // Daten kommen aus externer Datei, um Pflege zu erleichtern
  const mainSponsors = MAIN_SPONSORS;
  const goldSponsors = GOLD_SPONSORS;
  const officialSponsors = OFFICIAL_SPONSORS;

  // Hauptsponsoren: Logo + Name, groß und eindrucksvoll
  const renderMainSponsors = (list) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {list.map((sponsor, index) => {
        const keyId = `main-${index}`;
        const isVisible = visibleItems.includes(keyId);
        return (
          <a
            href={sponsor.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`bg-white dark:bg-slate-800 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300 p-8 flex flex-col items-center justify-center gap-4 min-h-[200px] group ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            } hover:border-primary dark:hover:border-primary-light`}
            key={keyId}
            data-index={keyId}
            style={{ transitionDelay: `${index * 0.1}s` }}
            aria-label={sponsor.name}
          >
            <div className="flex-shrink-0 w-32 h-32 flex items-center justify-center">
              <img
                src={sponsor.src.startsWith('/') || sponsor.src.startsWith('http') ? sponsor.src : process.env.PUBLIC_URL + '/' + sponsor.src}
                alt={sponsor.name}
                className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = process.env.PUBLIC_URL + "/images/placeholder.jpg";
                }}
              />
            </div>
            <h4 className="text-xl font-bold text-slate-900 dark:text-slate-100 text-center group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
              {sponsor.name}
            </h4>
          </a>
        );
      })}
    </div>
  );

  // Premiumsponsoren: Logo + Name, kleiner als Hauptsponsoren
  const renderPremiumSponsors = (list) => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {list.map((sponsor, index) => {
        const keyId = `gold-${index}`;
        const isVisible = visibleItems.includes(keyId);
        return (
          <a
            href={sponsor.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 p-6 flex flex-col items-center justify-center gap-3 min-h-[160px] group ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            } hover:border-primary dark:hover:border-primary-light`}
            key={keyId}
            data-index={keyId}
            style={{ transitionDelay: `${index * 0.1}s` }}
            aria-label={sponsor.name}
          >
            <div className="flex-shrink-0 w-24 h-24 flex items-center justify-center">
              <img
                src={sponsor.src.startsWith('/') || sponsor.src.startsWith('http') ? sponsor.src : process.env.PUBLIC_URL + '/' + sponsor.src}
                alt={sponsor.name}
                className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = process.env.PUBLIC_URL + "/images/placeholder.jpg";
                }}
              />
            </div>
            <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100 text-center group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
              {sponsor.name}
            </h4>
          </a>
        );
      })}
    </div>
  );


  const splitIntoColumns = (items, columns = 4) => {
    const result = Array.from({ length: columns }, () => []);
    items.forEach((item, idx) => {
      result[idx % columns].push(item);
    });
    return result;
  };

  // Offizielle Sponsoren: Nur Name + Link
  const renderOfficialSponsors = (list) => {
    const cols = splitIntoColumns(list, 4);
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cols.map((col, colIdx) => (
          <ul key={`official-col-${colIdx}`} className="space-y-2">
            {col.map((s, i) => (
              <li key={`${s.name}-${i}`}>
                <a 
                  href={s.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:text-primary dark:hover:text-primary-light hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md transition-colors border border-transparent hover:border-primary/20 dark:hover:border-primary-light/20"
                >
                  <i className="fas fa-external-link-alt text-xs mr-2 opacity-50"></i>
                  {s.name}
                </a>
              </li>
            ))}
          </ul>
        ))}
      </div>
    );
  };

  const sortSponsorsByName = (list) =>
    list
      .slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", i18n.language || undefined, { sensitivity: "base" }));

  const sortedMainSponsors = sortSponsorsByName(mainSponsors);
  const sortedGoldSponsors = sortSponsorsByName(goldSponsors);
  const sortedOfficialSponsors = sortSponsorsByName(officialSponsors);

  return (
    <section className="pt-12 pb-12 bg-slate-50 dark:bg-slate-900 transition-colors relative z-10" id="sponsors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("sponsors_partners.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          
          {/* Subheadline mit Handschlags-Icons */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <i className="fas fa-handshake text-primary dark:text-primary-light text-xl md:text-2xl opacity-70"></i>
            <p className="text-xl md:text-2xl text-slate-700 dark:text-slate-300 font-medium">
              {t("sponsors_partners.subheadline")}
            </p>
            <i className="fas fa-handshake text-primary dark:text-primary-light text-xl md:text-2xl opacity-70"></i>
          </div>
          
          {/* Haupttext */}
          <p className="text-slate-600 dark:text-slate-400 mt-4 mb-8 text-lg max-w-3xl mx-auto">
            {t("sponsors_partners.main_text")}
          </p>
          
          {/* Trennlinie mit Herz-Symbol */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex-1 h-px bg-gradient-to-l from-slate-300 to-transparent dark:from-slate-600 dark:to-transparent"></div>
            <div className="mx-4 flex items-center justify-center">
              <i className="fas fa-heart text-slate-400 dark:text-slate-500 text-lg"></i>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-300 to-transparent dark:from-slate-600 dark:to-transparent"></div>
          </div>
        </div>

        <div className="space-y-12" ref={containerRef}>
          {/* Hauptsponsoren */}
          {sortedMainSponsors.length > 0 && (
            <div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <i className="fas fa-trophy text-yellow-600 dark:text-yellow-400" aria-hidden="true"></i>
                </div>
                {t("sponsors_partners.main_title")}
              </h3>
              {renderMainSponsors(sortedMainSponsors)}
            </div>
          )}

          {/* Premiumsponsoren */}
          {sortedGoldSponsors.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <i className="fas fa-medal text-yellow-600 dark:text-yellow-400 text-sm" aria-hidden="true"></i>
                </div>
                {t("sponsors_partners.premium_title")}
              </h3>
              {renderPremiumSponsors(sortedGoldSponsors)}
            </div>
          )}

          {/* Offizielle Sponsoren */}
          {sortedOfficialSponsors.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                  <i className="fas fa-link text-primary dark:text-primary-light text-sm" aria-hidden="true"></i>
                </div>
                {t("sponsors_partners.official_title", "Offizielle Sponsoren")}
              </h3>
              {renderOfficialSponsors(sortedOfficialSponsors)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default SponsorsPartners;